import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MoreHorizontal, X } from 'lucide-react';
import { PHONE_QUERY, ResizeHandle, useMediaQuery } from '@bastion/ui';
import { ChatView } from '../ChatView';
import {
  useChat,
  useConversations,
  useConversationSession,
  useDeleteConversation,
  useRenameConversation,
} from '../hooks';
import { useAssistantPanelStore } from '../stores/chatStore';
import { useVisualViewportBox } from './useVisualViewportBox';
import styles from './AssistantPanel.module.css';

const ASSISTANT_DEFAULT_WIDTH = 380;
const ASSISTANT_MIN_WIDTH = 280;
/**
 * The widest right panel. A drag stops here, and the wide panel of the
 * webapp (`toggleWide`) is at most this wide.
 */
export const ASSISTANT_MAX_WIDTH = 960;
const ASSISTANT_SNAP_THRESHOLD = 200;
/**
 * Where the panel covers the page when `overlayBelowWide` is set: a sheet on
 * a phone, and a drawer on a touch screen from 761px to 1099.98px (design
 * system README, "Internal apps (Bastion)"). A mouse keeps the inline column
 * at 761px and up, so a narrow desktop window does not change.
 */
export const PANEL_COVERS_PAGE_QUERY =
  '(max-width: 760px), (pointer: coarse) and (max-width: 1099.98px)';

export interface AssistantPanelProps {
  isOpen: boolean;
  onToggle: () => void;
  width: number | null;
  onWidthChange: (width: number | null) => void;
  isResizing: boolean;
  onResizingChange: (resizing: boolean) => void;
  getAppState?: () => Record<string, unknown>;
  /** When true, a saved dashboard is open and its definition is included in chat app state. */
  dashboardAssistantActive?: boolean;
  /** Dashboard title for tooltip when `dashboardAssistantActive`. */
  dashboardAssistantLabel?: string | null;
  /**
   * When set (e.g. after confirming assistant-driven navigation), load this
   * conversation in the panel then invoke `onHandoffConsumed`.
   */
  handoffSessionId?: string | null;
  onHandoffConsumed?: () => void;
  onViewAllChats?: () => void;
  extraTabs?: AssistantPanelTab[];
  activeTabId?: string;
  onActiveTabChange?: (tabId: string) => void;
  /**
   * Where PANEL_COVERS_PAGE_QUERY matches, the panel is a layer over the
   * page: a sheet at 760px and under, and a drawer on a touch screen from
   * 761px to 1099.98px. The panel then has a close button and no resize
   * handle. The webapp sets it. The desktop app keeps the inline panel at
   * every width.
   */
  overlayBelowWide?: boolean;
}

export interface AssistantPanelTab {
  id: string;
  label: string;
  content: unknown;
  /** The title at the left of the panel header while the tab is active. */
  title?: unknown;
  /** The controls at the right of the panel header while the tab is active. */
  actions?: unknown;
}

export function AssistantPanel({
  isOpen,
  onToggle,
  width,
  onWidthChange,
  isResizing,
  onResizingChange,
  getAppState,
  dashboardAssistantActive = false,
  dashboardAssistantLabel = null,
  handoffSessionId = null,
  onHandoffConsumed,
  onViewAllChats,
  extraTabs = [],
  activeTabId = 'chat',
  onActiveTabChange,
  overlayBelowWide = false,
}: AssistantPanelProps) {
  const [panelSessionId, setPanelSessionId] = useState<string | undefined>(undefined);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLElement | null>(null);
  const isPhone = useMediaQuery(PHONE_QUERY);
  const coversPage = useMediaQuery(PANEL_COVERS_PAGE_QUERY);
  // A drag resizes only the inline panel. The handle measures its parent,
  // so it is not rendered where the panel is a drawer or a sheet.
  const showResizeHandle = !overlayBelowWide || !coversPage;

  useVisualViewportBox(panelRef, isOpen && isPhone);

  const {
    messages,
    isStreaming,
    error,
    sessionId: chatSessionId,
    sendMessage,
    stopStreaming,
    clearMessages,
    confirmToolCall,
    cancelToolCall,
    answerToolCall,
  } = useChat(getAppState, useAssistantPanelStore);

  const { data: conversations = [] } = useConversations();
  const renameConversation = useRenameConversation();
  const deleteConversation = useDeleteConversation();

  useConversationSession({
    sessionId: panelSessionId,
    sendMessage,
    store: useAssistantPanelStore,
  });

  useEffect(() => {
    if (!isOpen || !handoffSessionId) return;
    setPanelSessionId(handoffSessionId);
    onHandoffConsumed?.();
  }, [isOpen, handoffSessionId, onHandoffConsumed]);

  useEffect(() => {
    if (!menuOpen) return;
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (menuRef.current?.contains(target)) return;
      setMenuOpen(false);
    };
    // Escape closes only the menu. The handled event tells the app not to
    // close the panel too.
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      setMenuOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [menuOpen]);

  const handleResize = useCallback(
    (newWidth: number) => {
      if (newWidth < ASSISTANT_SNAP_THRESHOLD) {
        onToggle();
        onWidthChange(null);
      } else {
        onWidthChange(newWidth);
      }
    },
    [onToggle, onWidthChange],
  );

  const handleResizeStart = useCallback(() => {
    onResizingChange(true);
  }, [onResizingChange]);

  const handleResizeEnd = useCallback((_width: number) => {
    onResizingChange(false);
  }, [onResizingChange]);

  const handleDoubleClick = useCallback(() => {
    onWidthChange(null);
  }, [onWidthChange]);

  const handleClearConversation = useCallback(() => {
    stopStreaming();
    setPanelSessionId(undefined);
    clearMessages();
  }, [clearMessages, stopStreaming]);

  const activeSessionId = panelSessionId ?? chatSessionId;
  const activeConversation = useMemo(
    () => conversations.find((conversation) => conversation.session_id === activeSessionId) ?? null,
    [activeSessionId, conversations],
  );
  const canModifyActiveConversation = Boolean(activeConversation);
  const selectedTabId =
    activeTabId === 'chat' || extraTabs.some((tab) => tab.id === activeTabId)
      ? activeTabId
      : 'chat';
  const activeExtraTab = extraTabs.find((tab) => tab.id === selectedTabId) ?? null;
  const showTabs = extraTabs.length > 0;

  const handleRenameConversation = useCallback(() => {
    setMenuOpen(false);
    if (!activeSessionId || !activeConversation) return;
    const currentTitle = activeConversation?.title?.trim() ?? '';
    const next = window.prompt('Rename chat', currentTitle);
    const title = next?.trim();
    if (!title || title === currentTitle) return;
    renameConversation.mutate({ sessionId: activeSessionId, title });
  }, [activeConversation, activeSessionId, renameConversation]);

  const handleDeleteConversation = useCallback(() => {
    setMenuOpen(false);
    if (!activeSessionId || !activeConversation) return;
    if (!window.confirm('Delete this chat? This cannot be undone.')) return;
    deleteConversation.mutate(activeSessionId, {
      onSuccess: handleClearConversation,
    });
  }, [activeConversation, activeSessionId, deleteConversation, handleClearConversation]);

  const handleViewAllChats = useCallback(() => {
    setMenuOpen(false);
    onViewAllChats?.();
  }, [onViewAllChats]);

  const sidebarClasses = [
    styles.sidebar,
    isOpen ? styles.expanded : styles.collapsed,
    isResizing ? styles.resizing : '',
  ]
    .filter(Boolean)
    .join(' ');

  const expandedWidth = width ?? ASSISTANT_DEFAULT_WIDTH;
  const inlineStyle: React.CSSProperties | undefined =
    isOpen ? { width: expandedWidth } : undefined;

  const innerStyle: React.CSSProperties = {
    minWidth: expandedWidth,
    width: expandedWidth,
  };

  const layout = overlayBelowWide ? 'drawer' : undefined;

  // The close button shows where the panel covers the page
  // (PANEL_COVERS_PAGE_QUERY and AssistantPanel.module.css).
  const closeButton = overlayBelowWide ? (
    <button
      type="button"
      className={`${styles.actionButton} ${styles.closeButton}`}
      onClick={onToggle}
      title="Close panel"
      aria-label="Close panel"
      data-testid="panel-close"
    >
      <X className={styles.actionIcon} aria-hidden="true" />
    </button>
  ) : null;

  return (
    <>
      {/* Mobile backdrop when open */}
      {isOpen && (
        <div
          className={styles.mobileBackdrop}
          data-layout={layout}
          onClick={onToggle}
          aria-hidden="true"
        />
      )}
      <aside ref={panelRef} className={sidebarClasses} style={inlineStyle} data-layout={layout}>
      {isOpen ? (
        <div className={styles.panelInner} style={innerStyle}>
          {/* The tablist sits at the right for every tab, so it does not move
              when the tab changes. The Chat tab has no title. */}
          <div className={styles.header}>
            <div className={styles.headerTitle}>
              {showTabs ? (activeExtraTab?.title as never) : 'Chat'}
            </div>
            {showTabs ? (
              <div className={styles.tabList} role="tablist" aria-label="Sidebar tabs">
                <button
                  type="button"
                  role="tab"
                  aria-selected={selectedTabId === 'chat'}
                  className={`${styles.tabButton}${selectedTabId === 'chat' ? ` ${styles.tabButtonActive}` : ''}`}
                  onClick={() => onActiveTabChange?.('chat')}
                >
                  Chat
                </button>
                {extraTabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={selectedTabId === tab.id}
                    className={`${styles.tabButton}${selectedTabId === tab.id ? ` ${styles.tabButtonActive}` : ''}`}
                    onClick={() => onActiveTabChange?.(tab.id)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            ) : null}
            {activeExtraTab ? (
              <div className={styles.headerActions}>
                {activeExtraTab.actions as never}
                {closeButton}
              </div>
            ) : (
            <div className={styles.headerActions} ref={menuRef}>
              <button
                className={styles.actionButton}
                onClick={() => setMenuOpen((open) => !open)}
                title="Chat actions"
                aria-label="Chat actions"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                data-testid="panel-chat-actions"
              >
                <MoreHorizontal className={styles.actionIcon} />
              </button>
              {menuOpen ? (
                <div className={styles.menu} role="menu">
                  <button
                    type="button"
                    className={styles.menuItem}
                    role="menuitem"
                    onClick={handleRenameConversation}
                    disabled={!canModifyActiveConversation}
                  >
                    Rename
                  </button>
                  <button
                    type="button"
                    className={[styles.menuItem, styles.menuItemDanger].join(' ')}
                    role="menuitem"
                    onClick={handleDeleteConversation}
                    disabled={!canModifyActiveConversation}
                  >
                    Delete
                  </button>
                  <button
                    type="button"
                    className={styles.menuItem}
                    role="menuitem"
                    onClick={handleViewAllChats}
                  >
                    View all chats
                  </button>
                </div>
              ) : null}
              {closeButton}
            </div>
            )}
          </div>

          {activeExtraTab ? (
            <div className={styles.extraTabBody} role="tabpanel">
              {activeExtraTab.content as never}
            </div>
          ) : (
            <ChatView
              className={styles.chatCard}
              messages={messages}
              isStreaming={isStreaming}
              error={error}
              onSendMessage={sendMessage}
              onStopStreaming={stopStreaming}
              onConfirmToolCall={confirmToolCall}
              onCancelToolCall={cancelToolCall}
              onAnswerToolCall={answerToolCall}
              inputPlaceholder="Message..."
              emptyStateTitle="Chat"
              emptyStateDescription="Ask about your data, workflows, or next action."
              dashboardContextActive={dashboardAssistantActive}
              dashboardContextTitle={dashboardAssistantLabel}
            />
          )}

          {showResizeHandle ? (
            <ResizeHandle
              side="left"
              onResize={handleResize}
              onResizeStart={handleResizeStart}
              onResizeEnd={handleResizeEnd}
              onDoubleClick={handleDoubleClick}
              minWidth={ASSISTANT_MIN_WIDTH}
              maxWidth={ASSISTANT_MAX_WIDTH}
            />
          ) : null}
        </div>
      ) : null}
    </aside>
    </>
  );
}
